<rt-dialog-header [headingTpl]="titleTpl" [actionsSlot]="headerActions" />

<ng-template #headerActions>
  @if (isEdit()) {
    <rt-button variant="tertiary" (click)="cancelEdit()" margin-right-small-x>
      {{ 'tlc.common.cancel' | translate }}
    </rt-button>
    <rt-button variant="primary" (click)="save()">
      {{ 'tlc.positions.position_history_dialog.save' | translate }}
    </rt-button>
  } @else if (canEdit()) {
    <rt-button variant="tertiary" (click)="enterEdit()">
      <rt-icon name="edit" />
      <span>{{ 'tlc.positions.position_history_dialog.edit' | translate }}</span>
    </rt-button>
  }
</ng-template>

<ng-template #titleTpl>
  <span class="header-heading">
    <rt-avatar
      [type]="memberAvatarUrl() ? 'image' : 'initials'"
      [imageSrc]="memberAvatarUrl()"
      [fullName]="memberName()"
      context="admin"
    />
    <span class="header-text">
      <span class="member-name">{{ memberName() }}</span>
      <span class="dialog-title">{{
        'tlc.positions.position_history_dialog.header' | translate
      }}</span>
    </span>
  </span>
</ng-template>

<rt-dialog-body variant="white">
  @if (history(); as history) {
    @if (isEdit()) {
      <div class="edit-form">
        <div class="timeline">
          <!-- Scheduled change -->
          <section class="timeline-section scheduled">
            <span class="dot" [class.hollow]="!scheduledRow()"></span>
            <div class="section-body">
              @if (scheduledRow(); as row) {
                <span class="section-title">
                  {{ 'tlc.positions.position_history_dialog.scheduled' | translate }}
                </span>
                <tlc-position-history-edit-card
                  [form]="row"
                  [positionItems]="positionItems()"
                  dateDirection="today-future"
                  [showDelete]="true"
                  (deleteClicked)="removeScheduled()"
                />
              } @else {
                <span class="section-title muted">
                  {{ 'tlc.positions.position_history_dialog.no_scheduled' | translate }}
                </span>
                <rt-button variant="tertiary" class="schedule-button" (click)="scheduleChange()">
                  <rt-icon name="calendar-blank" />
                  <span>{{
                    'tlc.positions.position_history_dialog.schedule_change' | translate
                  }}</span>
                </rt-button>
                <span class="hint">
                  {{ 'tlc.positions.position_history_dialog.schedule_hint' | translate }}
                </span>
              }
            </div>
          </section>

          <!-- Current position -->
          <section class="timeline-section">
            <span class="dot"></span>
            <div class="section-body">
              <span class="section-title">
                {{ 'tlc.positions.position_history_dialog.current_position' | translate }}
              </span>
              <tlc-position-history-edit-card
                [form]="currentRow()"
                [positionItems]="positionItems()"
                dateDirection="today-past"
              />
            </div>
          </section>

          <!-- Previous positions -->
          <section class="timeline-section">
            <span class="dot"></span>
            <div class="section-body">
              <span class="section-title">
                {{ 'tlc.positions.position_history_dialog.previous_positions' | translate }}
              </span>
              @for (row of previous.controls; track row; let i = $index) {
                <tlc-position-history-edit-card
                  [form]="row"
                  [positionItems]="positionItems()"
                  dateDirection="today-past"
                  [showDelete]="true"
                  (deleteClicked)="removePrevious(i)"
                />
              }
              <rt-button variant="plain" class="add-button" (click)="addPrevious()">
                <rt-icon name="add" />
                <span>{{ 'tlc.positions.position_history_dialog.add_previous' | translate }}</span>
              </rt-button>
            </div>
          </section>
        </div>
      </div>
    } @else {
      <div class="timeline">
        @if (history.scheduled; as scheduled) {
          <section class="timeline-section scheduled">
            <span class="dot hollow"></span>
            <div class="section-body">
              <span class="section-title">
                {{ 'tlc.positions.position_history_dialog.scheduled' | translate }}
              </span>
              <div class="position-card">
                <div class="position-icon">
                  <rt-icon name="positions" colorToken="colorIconPrimaryDefault" />
                </div>
                <div class="position-info">
                  <div class="position-name-row">
                    <span class="position-name">{{ scheduled.positionName }}</span>
                    <rt-badge
                      color="requested"
                      iconName="clock"
                      [text]="'tlc.positions.position_history_dialog.scheduled_badge' | translate"
                    />
                  </div>
                  <span class="position-date">{{ startedLabel(scheduled) }}</span>
                </div>
              </div>
            </div>
          </section>
        }

        <section class="timeline-section">
          <span class="dot"></span>
          <div class="section-body">
            <span class="section-title">
              {{ 'tlc.positions.position_history_dialog.current_position' | translate }}
            </span>
            @if (history.current; as current) {
              <div class="position-card">
                <div class="position-icon">
                  <rt-icon name="positions" colorToken="colorIconPrimaryDefault" />
                </div>
                <div class="position-info">
                  <div class="position-name-row">
                    <span class="position-name">{{ current.positionName }}</span>
                    <rt-badge
                      color="primary"
                      [text]="'tlc.positions.position_history_dialog.current_badge' | translate"
                    />
                  </div>
                  <span class="position-date">{{ startedLabel(current) }}</span>
                </div>
              </div>
            }
          </div>
        </section>

        <section class="timeline-section">
          <span class="dot"></span>
          <div class="section-body">
            <span class="section-title">
              {{ 'tlc.positions.position_history_dialog.previous_positions' | translate }}
            </span>
            @for (item of history.previous; track item.id) {
              <div class="position-card muted">
                <div class="position-icon">
                  <rt-icon name="positions" colorToken="colorIconLowDefault" />
                </div>
                <div class="position-info">
                  <span class="position-name">{{ item.positionName }}</span>
                  <span class="position-date">{{ startedLabel(item) }}</span>
                </div>
              </div>
            }
          </div>
        </section>
      </div>
    }
  }
</rt-dialog-body>
